import { BreakPointCondition, Table, useBreakpointIf } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Box, FlatList } from 'native-base';
import React from 'react';
import { Dimensions, DimensionValue } from 'react-native';

import {
  TableHeadContainer,
  TableHeadItem,
  TableHeadItemRecurse,
} from '../../../../../components/TableHead';
import { ProductTransactionFlow } from '../../../../../store/transactionFlow/types';
import { useTableSort } from '../_hooks/useTableSort';

import { ProductTableMobile, ProductTableMobileProps } from './components/Mobile';
import { AssetWithDocument } from './components/Mobile/components/AssetPrincipalCard';
import { ProductTableTablet } from './components/Tablet';
import { NotFoundTable, ProductTableProps } from './constants';

export interface DynamicProductTableProps<T> extends ProductTableProps {
  ProductItem: React.FC<IDynamicProductSearchItem<T>>;
  mobileFields: ProductTableMobileProps<T>['fields'];
  tableHeadItems: TableHeadItemRecurse[];
  tabletColumns: number;
  tabletRows?: number;
  onSelectClient: (cpfCnpj: string) => void;
}

export interface IDynamicProductSearchItem<T> {
  item: T;
  product: ProductTransactionFlow;
  onSelectClient: (cpfCnpj: string) => void;
}

const ItemSeparator = () => <Box height={Spacing['stack.nano']} />;

export const DynamicProductTable = <T extends AssetWithDocument>({
  product,
  tableHeadItems,
  ProductItem,
  mobileFields,
  tabletColumns,
  tabletRows,
  onSelectClient,
  productData,
}: DynamicProductTableProps<T> & { productData: any }) => {
  const columns = tableHeadItems.length;
  const maxHeight = `${Dimensions.get('window').height - 295}px` as DimensionValue;
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const { sortedData, sortColumn, sortDirection, handleSort } = useTableSort<T>({
    initialData: (productData ?? []) as T[],
  });

  const renderTableHead = () => (
    <TableHeadContainer px={Spacing['inline.xxxs']}>
      {tableHeadItems.map((item, index) => (
        <TableHeadItem
          key={`${item.title}-${index}`}
          {...item}
          value={item.orderValue ? String(item.orderValue) : undefined}
          orderBy={
            sortColumn
              ? {
                  value: String(sortColumn),
                  direction: sortDirection === 'asc' ? 'asc' : 'desc',
                }
              : undefined
          }
          onPress={() => item.orderValue && handleSort(item.orderValue as keyof T)}
        />
      ))}
    </TableHeadContainer>
  );

  if (isMobile) {
    return (
      <ProductTableMobile data={sortedData} fields={mobileFields} onSelectClient={onSelectClient} />
    );
  }

  if (isTablet) {
    return (
      <ProductTableTablet
        data={sortedData}
        fields={mobileFields}
        columns={tabletColumns}
        product={product}
        rows={tabletRows}
        onSelectClient={onSelectClient}
      />
    );
  }

  if (sortedData.length === 0) {
    return <NotFoundTable />;
  }

  return (
    <Box overflow={'hidden'}>
      <Table isLoading={false} cols={columns} rows={5}>
        {renderTableHead()}

        <FlatList
          windowSize={20}
          maxToRenderPerBatch={20}
          updateCellsBatchingPeriod={5}
          data={sortedData}
          contentContainerStyle={{ maxHeight }}
          showsVerticalScrollIndicator={false}
          onEndReachedThreshold={1.5}
          ItemSeparatorComponent={ItemSeparator}
          renderItem={({ item }) => (
            <ProductItem product={product} item={item} onSelectClient={onSelectClient} />
          )}
        />
      </Table>
    </Box>
  );
};
